/**
 * Health indicator pill — shows client health state with dot + label.
 */

interface HealthConfig {
  label: string
  color: string
  bg: string
  border: string
}

const HEALTH_MAP = {
  good: {
    label: 'Healthy',
    color: 'var(--state-success)',
    bg: 'var(--success-soft)',
    border: 'rgba(63,182,139,0.35)',
  },
  watch: {
    label: 'Watch',
    color: 'var(--state-warning)',
    bg: 'var(--warning-soft)',
    border: 'rgba(224,169,59,0.35)',
  },
  'at-risk': {
    label: 'At risk',
    color: 'var(--state-danger)',
    bg: 'var(--danger-soft)',
    border: 'rgba(229,72,77,0.35)',
  },
} satisfies Record<string, HealthConfig>

const DEFAULT_CFG: HealthConfig = HEALTH_MAP.good

export function getHealthConfig(health: string): HealthConfig {
  return (HEALTH_MAP as Record<string, HealthConfig>)[health] ?? DEFAULT_CFG
}

interface HealthBadgeProps {
  health: string
  size?: 'sm' | 'md'
}

export function HealthBadge({ health, size = 'md' }: HealthBadgeProps) {
  const cfg = getHealthConfig(health)
  const pad = size === 'sm' ? '2px 8px' : '4px 10px'
  const fs = size === 'sm' ? 11 : 12

  return (
    <span
      className="health-badge"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: pad,
        borderRadius: 'var(--radius-pill)',
        fontSize: fs,
        fontWeight: 500,
        color: cfg.color,
        background: cfg.bg,
        border: `1px solid ${cfg.border}`,
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: cfg.color,
          flexShrink: 0,
        }}
      />
      {cfg.label}
    </span>
  )
}

export { HEALTH_MAP }
export type { HealthConfig }
